import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, PackageOpen, Pencil, X, Upload, Search } from "lucide-react";
import { compressImageFile } from "../../lib/imageUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VISUALS = ["bottle", "oil", "spray", "dispenser", "tube", "tin", "bar", "kitbox", "comb", "box", "jerrycan", "mop", "dustpan", "bucket", "machine", "diffuser"];

const inputCls = "w-full bg-[#FAF8FC] border border-[#221533]/15 rounded-md px-4 py-2.5 text-sm focus:border-[#8E5A9E] transition-colors";
const labelCls = "font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/45 block mb-1.5";

const EditModal = ({ product, categories, token, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: product.name || "",
    category: product.category || categories[0]?.slug || "",
    pack_size: product.pack_size || "",
    moq: product.moq || "",
    price: product.price || "",
    description: product.description || "",
    application: product.application || "",
    group: product.group || "",
    visual: product.visual || "bottle",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let image_path = product.image_path || "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/admin/upload`, fd, { headers });
        image_path = up.data.path;
      }
      await axios.put(`${API}/admin/products/${product.slug}`, { ...form, image_path }, { headers });
      toast.success(`${form.name} updated — website par live hai`);
      onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="edit-product-modal" className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8"
      >
        <div className="flex items-center justify-between mb-5">
          <p className="font-clash text-2xl font-semibold text-[#221533]">Edit Product</p>
          <button type="button" data-testid="edit-modal-close" onClick={onClose} className="w-9 h-9 flex items-center justify-center border border-[#221533]/15 rounded-md hover:border-[#E63946] hover:text-[#E63946] transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="space-y-3.5">
          <div>
            <label className={labelCls}>Product Name *</label>
            <input data-testid="edit-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Category *</label>
            <select data-testid="edit-category-select" value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`}>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Pack Size</label>
              <input data-testid="edit-packsize-input" value={form.pack_size} onChange={set("pack_size")} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>MOQ</label>
              <input data-testid="edit-moq-input" value={form.moq} onChange={set("moq")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Price (optional)</label>
            <input data-testid="edit-price-input" value={form.price} onChange={set("price")} placeholder="e.g. ₹450 / 5 L can" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea data-testid="edit-description-input" rows={2} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className={labelCls}>Sub-category / Group (optional)</label>
            <input data-testid="edit-group-input" value={form.group} onChange={set("group")} placeholder="e.g. Kitchen Care" list="edit-group-suggestions" className={inputCls} />
            <datalist id="edit-group-suggestions">
              {[...new Set((categories.find((c) => c.slug === form.category)?.products || []).map((p) => p.group).filter(Boolean))].map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </div>
          <div>
            <label className={labelCls}>Application</label>
            <input data-testid="edit-application-input" value={form.application} onChange={set("application")} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Icon Style</label>
            <select data-testid="edit-visual-select" value={form.visual} onChange={set("visual")} className={`${inputCls} appearance-none`}>
              {VISUALS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Replace Photo (optional)</label>
            <input data-testid="edit-photo-input" type="file" accept="image/*" onChange={async (e) => {
              const f = e.target.files?.[0] || null;
              if (f) {
                const compressed = await compressImageFile(f);
                setFile(compressed);
              } else {
                setFile(null);
              }
            }} className="w-full text-sm text-[#221533]/60 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#8E5A9E]/10 file:text-[#74427F] file:font-semibold hover:file:bg-[#8E5A9E]/20 file:cursor-pointer" />
          </div>
        </div>
        <button
          data-testid="edit-save-btn"
          type="submit"
          disabled={saving}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#8E5A9E] hover:bg-[#74427F] disabled:opacity-50 text-white font-mono2 text-xs tracking-[0.25em] uppercase py-4 rounded-md transition-colors active:scale-[0.98]"
        >
          <Upload size={14} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </form>
    </div>
  );
};

export const CustomProductList = ({ token, refreshKey }) => {
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = (products || []).filter(
    (p) =>
      !q ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q) ||
      (p.pack_size || "").toLowerCase().includes(q)
  );

  const load = () => {
    axios
      .get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setProducts(res.data.products))
      .catch(() => setProducts([]));
  };

  useEffect(load, [token, refreshKey]);

  useEffect(() => {
    axios.get(`${API}/catalogue`).then((res) => setCategories(res.data.categories)).catch(() => {});
  }, []);

  const del = async (slug) => {
    try {
      await axios.delete(`${API}/admin/products/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Product website se hata diya gaya");
      setProducts((p) => p.filter((x) => x.slug !== slug));
    } catch {
      toast.error("Could not delete product");
    }
  };

  if (products === null) {
    return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="h-40 bg-white border border-[#221533]/10 rounded-xl animate-pulse" />)}</div>;
  }

  if (!products.length) {
    return (
      <div data-testid="products-empty" className="text-center py-20 text-[#221533]/45">
        <PackageOpen size={36} className="mx-auto mb-4 text-[#8E5A9E]/50" />
        <p className="font-light">No products found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="relative mb-5 max-w-md">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#221533]/40" />
        <input
          data-testid="admin-product-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${products.length} products — name, category, pack size…`}
          className="w-full bg-white border border-[#221533]/15 rounded-full pl-11 pr-4 py-3 text-sm text-[#221533] placeholder-[#221533]/40 focus:border-[#8E5A9E] transition-colors"
        />
      </div>
      <div data-testid="custom-products-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <div key={p.slug} data-testid={`custom-product-${p.slug}`} className="bg-white border border-[#221533]/10 rounded-xl overflow-hidden">
            <div className="h-60 bg-[#FAF8FC] spotlight flex items-center justify-center overflow-hidden">
              {p.image_url ? (
                <img src={`${BACKEND}${p.image_url}`} alt={p.name} loading="lazy" decoding="async" className="h-full w-auto object-contain" />
              ) : (
                <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#221533]/35">icon: {p.visual}</span>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-clash text-lg font-medium truncate">{p.name}</p>
                  <p className="font-mono2 text-[10px] tracking-[0.15em] uppercase text-[#221533]/45 mt-1">{p.pack_size || "—"} · {p.category}</p>
                  {p.price && <p className="text-sm font-semibold text-[#74427F] mt-1">{p.price}</p>}
                </div>
                {p.source === "custom" && (
                  <span className="shrink-0 font-mono2 text-[9px] tracking-[0.15em] uppercase bg-[#8E5A9E]/10 text-[#74427F] px-2 py-1 rounded-full">New</span>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  data-testid={`edit-product-${p.slug}`}
                  onClick={() => setEditing(p)}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#8E5A9E]/40 text-[#74427F] hover:bg-[#8E5A9E] hover:text-white text-xs font-manrope font-semibold uppercase tracking-wide px-3 py-2.5 rounded-md transition-colors active:scale-95"
                >
                  <Pencil size={13} /> Edit
                </button>
                <button
                  data-testid={`delete-product-${p.slug}`}
                  onClick={() => del(p.slug)}
                  className="inline-flex items-center justify-center gap-2 border border-[#221533]/15 text-[#221533]/45 hover:text-[#E63946] hover:border-[#E63946] text-xs font-manrope font-semibold uppercase tracking-wide px-3 py-2.5 rounded-md transition-colors active:scale-95"
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <EditModal
          product={editing}
          categories={categories}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </>
  );
};
